import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * Floating dictation button (admin-only). Uses the browser's native
 * SpeechRecognition API — zero cost, no backend, no API keys.
 *
 * Behavior:
 *  - Click once to start listening. The button turns red and pulses.
 *  - Speak. A small bubble above the button shows what it hears live.
 *  - Click again to stop. The final transcript is inserted at the caret
 *    of whichever <input>, <textarea>, or contentEditable element was
 *    focused BEFORE the mic button was clicked.
 *
 * Supported in Chrome, Edge, Safari (desktop + mobile). Firefox not yet.
 */

const isSpeechSupported = () =>
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

const EDITABLE_SELECTOR =
  'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=file]):not([type=submit]):not([type=button]), textarea, [contenteditable=""], [contenteditable="true"]';

const DictationButton = () => {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef(null);
  const targetElRef = useRef(null); // the input/textarea/contenteditable to insert into

  // Track the most recently focused editable element on the page, so the mic
  // knows where to insert the transcript (the mic button itself steals focus
  // on click — we remember what was focused BEFORE that).
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest && t.closest('[data-dictation-ui]')) return; // ignore our own UI
      if (t.matches && t.matches(EDITABLE_SELECTOR)) {
        targetElRef.current = t;
      }
    };
    document.addEventListener('focusin', handler, true);
    return () => document.removeEventListener('focusin', handler, true);
  }, []);

  const insertText = (text) => {
    if (!text) return;
    const el = targetElRef.current;
    if (!el || !document.body.contains(el)) {
      // No target — copy to clipboard as a fallback so the words aren't lost
      try {
        navigator.clipboard.writeText(text);
        toast.success('No text field was focused. Copied to clipboard instead.');
      } catch {
        toast.info(`Heard: "${text}"`);
      }
      return;
    }

    // Refocus the real target (mic click moved focus to the button)
    try { el.focus(); } catch { /* noop */ }

    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const before = el.value.slice(0, start);
      const after = el.value.slice(end);
      const needsSpace = before && !/\s$/.test(before) ? ' ' : '';
      const insert = needsSpace + text;
      const next = before + insert + after;

      // Use the native setter so React's onChange still fires on controlled inputs
      const proto = el.tagName === 'INPUT' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype;
      const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
      setter.call(el, next);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      const caret = (before + insert).length;
      try { el.setSelectionRange(caret, caret); } catch { /* noop */ }
    } else if (el.isContentEditable) {
      // Insert at caret inside a contentEditable (used by PageEditable etc.)
      const sel = window.getSelection();
      if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const needsSpace = range.startContainer && range.startContainer.nodeType === 3 &&
          range.startOffset > 0 && !/\s$/.test(range.startContainer.textContent.slice(0, range.startOffset));
        const node = document.createTextNode((needsSpace ? ' ' : '') + text);
        range.insertNode(node);
        range.setStartAfter(node);
        range.setEndAfter(node);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.textContent = (el.textContent || '') + (el.textContent ? ' ' : '') + text;
      }
      el.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
    }
  };

  const start = () => {
    const Impl = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Impl) {
      toast.error('Your browser doesn\'t support dictation. Try Chrome or Safari.');
      return;
    }
    try {
      const rec = new Impl();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = (navigator.language || 'en-US');
      rec.onresult = (e) => {
        let finalText = '';
        let interimText = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interimText += r[0].transcript;
        }
        if (finalText) {
          insertText(finalText.trim());
          setInterim('');
        } else {
          setInterim(interimText);
        }
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          toast.error('Microphone blocked. Please allow mic access and try again.');
        } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
          toast.error(`Dictation error: ${e.error}`);
        }
        setListening(false);
        setInterim('');
      };
      rec.onend = () => {
        setListening(false);
        setInterim('');
      };
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch (err) {
      toast.error(err.message || 'Could not start dictation');
    }
  };

  const stop = () => {
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
    setListening(false);
  };

  const toggle = (e) => {
    // Don't let the click steal focus from the target input
    e.preventDefault();
    if (listening) stop(); else start();
  };

  if (!isAuthenticated || !isAdmin) return null;
  if (!isSpeechSupported()) return null; // Hide on Firefox etc.

  return (
    <div data-dictation-ui className="fixed left-4 bottom-36 z-[61]" data-testid="dictation-button-wrap">
      {interim && (
        <div
          className="absolute left-0 bottom-full mb-2 max-w-[320px] px-3 py-2 rounded-lg bg-black/90 text-white text-sm shadow-xl border border-yellow-500/40"
          data-testid="dictation-interim"
        >
          <span className="opacity-60">Hearing: </span>
          {interim}
        </div>
      )}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
        data-testid="dictation-toggle-btn"
        aria-label={listening ? 'Stop dictation' : 'Start dictation'}
        className={`group flex items-center gap-2 px-4 py-3 rounded-full font-bold shadow-2xl transition-transform hover:scale-105 ${
          listening
            ? 'bg-red-600 text-white shadow-red-500/40 animate-pulse'
            : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-black shadow-emerald-500/40'
        }`}
      >
        {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        <span className="text-sm tracking-wide">{listening ? 'Stop' : 'Dictate'}</span>
      </button>
    </div>
  );
};

export default DictationButton;
